// マスタCSVエクスポートで配るファイルの中身を組み立てるモジュール。
// 画面で選ばれたマスタを「取り直した内容から書き出せた結果」へ変換するところまでを受け持つ。
// ブラウザの保存操作やアンカー要素の組み立ては画面側の担当で、ここには持ち込まない。

import type { SliceKey } from "../../../../api/dbSlice";
import type { ExtendedDB } from "../../../../types/extendedDB";
import { csvSerialize } from "../../../../util/csvIo";
import { readProp } from "../../../../util/typed";
import type { MasterCsvColumn } from "./masterCsvColumns";
import { cellOfValue, csvColumnsOf } from "./masterCsvColumns";
import type { MasterScreenKey } from "./masterCsvTargets";
import { MASTER_CSV_TABLE_ROW_LABEL, collectionKeysOfScreenKeys, screenLabelOfCollectionKey } from "./masterCsvTargets";

// ダウンロードする1ファイル分。fileName はテーブル名そのもので、zip に詰めるときの名前にもなる。
// どのマスタのデータかは本文1行目の識別行が持つため、ファイル名は取り込みの判定には使われない。
export type MasterCsvFile = { fileName: string; text: string };

// 出力元になるマスタの読み出し口。画面からはブラウザの保存領域を読んだ ExtendedDB をそのまま渡せる。
// 部分形にしてあるのは、検証時に必要なマスタだけを持つ小さな値を渡せるようにするため。
export type MasterCsvExportSource = Partial<ExtendedDB>;

// 選ばれたマスタを書き出そうとした結果。
// files は書き出すファイルが1つ以上あるとき、empty は選んだマスタがすべて0件だったとき、
// missing は取り直した内容に選んだテーブルのキーそのものが無かったときになる。
export type MasterCsvExportOutcome =
  | { name: "files"; files: MasterCsvFile[] }
  | { name: "empty" }
  | { name: "missing"; message: string };

// 複数マスタをまとめて配るときのファイル名の接頭辞。
const ZIP_FILE_NAME_PREFIX = "okumura-masters-";

// 出力対象の1行。どのマスタの行でも扱えるよう、列名で値を引ける形として受け取る。
type MasterCsvSourceRow = Record<string, unknown>;

// 論理削除された行は、マスタ上は存在しない扱いのため出力しない。
function activeRowsOf(rows: readonly MasterCsvSourceRow[]): MasterCsvSourceRow[] {
  return rows.filter((row) => readProp(row, "deleted_at") == null);
}

// 1行分を、列名をキーにしたセル文字列の集まりへ組み替える。
function cellRecordOf(row: MasterCsvSourceRow, columns: readonly MasterCsvColumn[]): Record<string, string> {
  const cells: Record<string, string> = {};
  for (const column of columns) {
    cells[column.name] = cellOfValue(readProp(row, column.name), column);
  }
  return cells;
}

// CSVの1行目に置く識別行。取り込み時にどのマスタのデータかを決める唯一の手がかりになる。
function tableRowOf(collectionKey: SliceKey): string[] {
  return [MASTER_CSV_TABLE_ROW_LABEL, collectionKey];
}

// 1テーブル分のCSV本文を作る。1行目が識別行、2行目がヘッダ行、3行目以降がデータ行になる。
// 呼び出し側で有効な行が1件以上あることを確かめてから使う想定のため、ここでは行の有無を判定しない。
function csvTextOf(collectionKey: SliceKey, activeRows: readonly MasterCsvSourceRow[]): string {
  const columns = csvColumnsOf(collectionKey);
  const columnNames = columns.map((column) => column.name);
  const cellRecords = activeRows.map((row) => cellRecordOf(row, columns));
  return csvSerialize(cellRecords, columnNames, tableRowOf(collectionKey));
}

const MISSING_MASTERS_MESSAGE_PREFIX = "最新のマスタを取得できなかったため、書き出しを中止しました。取得できなかったマスタ: ";

// キーが無かったテーブルを、選択画面の日本語名へ変換して案内文にする。
// 同じ画面が複数テーブルを束ねていて、そのうち複数のキーが無くても、画面名は1回だけ出す。
function missingMastersMessageOf(missingCollectionKeys: readonly SliceKey[]): string {
  const labels: string[] = [];
  for (const collectionKey of missingCollectionKeys) {
    const label = screenLabelOfCollectionKey(collectionKey);
    if (!labels.includes(label)) {
      labels.push(label);
    }
  }
  return `${MISSING_MASTERS_MESSAGE_PREFIX}${labels.join("、")}`;
}

// 選ばれた画面から、配るファイルの中身を作る。ファイルはテーブル単位で1つずつになる。
// 1つの画面が複数テーブルを束ねている場合は、その分だけファイルが増える。
//
// ファイル名にテーブル名を使うのは、利用者が中身を推測しやすいようにするための便宜にすぎない。
// 取り込み側はファイル名を一切見ず、本文1行目の識別行だけでテーブルを決めるため、名前を変えても取り込める。
//
// 判定は次の順に行う。
// 1. 選んだテーブルのキーが取り直した内容に1つでも無ければ、書き出し全体を中止する(missing)。
// 2. キーはすべて揃っているが、論理削除を除いた有効な行がどのテーブルも0件なら、何も出力しない(empty)。
// 3. 有効な行が1件以上あるテーブルだけをファイルにする(files)。0件のテーブルは含めない。
export function masterCsvFilesOf(db: MasterCsvExportSource, screenKeys: readonly MasterScreenKey[]): MasterCsvExportOutcome {
  const collectionKeys = collectionKeysOfScreenKeys(screenKeys);
  const missingCollectionKeys = collectionKeys.filter((collectionKey) => db[collectionKey] === undefined);
  if (missingCollectionKeys.length > 0) {
    return { name: "missing", message: missingMastersMessageOf(missingCollectionKeys) };
  }

  const files: MasterCsvFile[] = [];
  for (const collectionKey of collectionKeys) {
    const rows = db[collectionKey];
    if (rows !== undefined) {
      const activeRows = activeRowsOf(rows);
      if (activeRows.length > 0) {
        files.push({ fileName: `${collectionKey}.csv`, text: csvTextOf(collectionKey, activeRows) });
      }
    }
  }

  if (files.length === 0) {
    return { name: "empty" };
  }
  return { name: "files", files };
}

// ファイル名に埋め込む日付。区切り文字を取り除いて数字だけの並びにする。
function compactDateTextOf(todayText: string): string {
  return todayText.replace(/[^0-9]/g, "");
}

// 拡張子を除いたファイル名。テーブル名をそのまま取り出すために使う。
function fileNameWithoutExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex > 0) {
    return fileName.slice(0, lastDotIndex);
  }
  return fileName;
}

// 利用者がダウンロードするファイルの名前を決める。
// マスタが1つだけならCSVをそのまま配り、2つ以上あればまとめたzipとして配る。
// 現在時刻はここでは読まず、呼び出し側が決めた日付文字列だけを使う。
export function exportFileNameOf(files: readonly MasterCsvFile[], todayText: string): string {
  const dateText = compactDateTextOf(todayText);
  const singleFile = files[0];
  if (files.length === 1 && singleFile !== undefined) {
    return `${fileNameWithoutExtension(singleFile.fileName)}-${dateText}.csv`;
  }
  return `${ZIP_FILE_NAME_PREFIX}${dateText}.zip`;
}
